import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Grid, Plus, Save, Eye, EyeOff, Trash2, Copy, 
  Settings, Layout, Move, RefreshCw 
} from 'lucide-react';
import { Responsive } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import api from '../../../services/api';
import LoadingSpinner from '../../../components/LoadingSpinner';

const DashboardGridManager = () => {
  const [selectedRole, setSelectedRole] = useState('master_admin');
  const [config, setConfig] = useState(null);
  const [widgetLibrary, setWidgetLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showWidgetLibrary, setShowWidgetLibrary] = useState(false);
  const [containerWidth, setContainerWidth] = useState(1200);

  const roles = [
    { code: 'master_admin', name: 'Master Admin' },
    { code: 'admin', name: 'Admin' },
    { code: 'support', name: 'Support' },
    { code: 'account', name: 'Account' },
    { code: 'billing', name: 'Billing' },
    { code: 'operator', name: 'Operator' },
    { code: 'user', name: 'User' },
  ];

  useEffect(() => {
    loadDashboardConfig();
    loadWidgetLibrary();
  }, [selectedRole]);

  useEffect(() => {
    const handleResize = () => {
      const container = document.querySelector('.layout')?.parentElement;
      if (container) {
        setContainerWidth(container.offsetWidth);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadDashboardConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/dashboard/configs/${selectedRole}`);
      const configData = response.data;
      // Ensure widgets array exists
      if (configData && !configData.widgets) {
        configData.widgets = [];
      }
      setConfig(configData);
    } catch (error) {
      console.error('Failed to load dashboard config:', error);
      // Set default config on error
      setConfig({
        id: null,
        config_name: 'Default Config',
        layout_type: 'grid',
        columns: 4,
        gap: 16,
        is_active: true,
        widgets: []
      });
    } finally {
      setLoading(false);
    }
  };

  const loadWidgetLibrary = async () => {
    try {
      const response = await api.get(`/admin/dashboard/widget-library/${selectedRole}`);
      setWidgetLibrary(response.data.widget_types || []);
    } catch (error) {
      console.error('Failed to load widget library:', error);
    }
  };

  const handleLayoutChange = (layout) => {
    if (!config || !config.widgets) return;

    const updatedWidgets = config.widgets.map(widget => {
      const layoutItem = layout.find(l => l.i === `widget-${widget.id}`);
      if (layoutItem) {
        return {
          ...widget,
          position_x: layoutItem.x,
          position_y: layoutItem.y,
          width: layoutItem.w,
          height: layoutItem.h,
        };
      }
      return widget;
    });

    setConfig({
      ...config,
      widgets: updatedWidgets,
    });
  };

  const handleSaveConfiguration = async () => {
    if (!config || !config.widgets) return;
    
    try {
      setSaving(true);

      const widgetConfigs = config.widgets.map((widget, index) => ({
        widget_type_id: widget.widget_type_id,
        widget_title: widget.widget_title,
        position_x: widget.position_x,
        position_y: widget.position_y,
        width: widget.width,
        height: widget.height,
        min_width: widget.min_width || 1,
        min_height: widget.min_height || 1,
        max_width: widget.max_width || 12,
        max_height: widget.max_height || 12,
        is_resizable: widget.is_resizable !== false,
        is_draggable: widget.is_draggable !== false,
        is_visible: widget.is_visible !== false,
        custom_config: widget.custom_config || {},
        display_order: index,
      }));

      await api.put(`/admin/dashboard/configs/${config.id}`, {
        config_name: config.config_name,
        layout_type: config.layout_type,
        columns: config.columns,
        gap: config.gap,
        widgets: widgetConfigs,
      });

      alert('Dashboard configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save configuration:', error);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAddWidget = (widgetType) => {
    if (!config) return;

    const widgets = config.widgets || [];
    const newWidget = {
      id: Date.now(), // Temporary ID
      config_id: config.id,
      widget_type_id: widgetType.id,
      widget_title: widgetType.widget_name,
      position_x: 0,
      position_y: widgets.length,
      width: 1,
      height: 1,
      min_width: 1,
      min_height: 1,
      max_width: 4,
      max_height: 4,
      is_resizable: true,
      is_draggable: true,
      is_visible: true,
      display_order: widgets.length,
      widget_type: widgetType,
    };

    setConfig({
      ...config,
      widgets: [...widgets, newWidget],
    });
    setShowWidgetLibrary(false);
  };

  const handleRemoveWidget = (widgetId) => {
    if (!config || !config.widgets) return;
    if (!window.confirm('Are you sure you want to remove this widget?')) return;

    setConfig({
      ...config,
      widgets: config.widgets.filter(w => w.id !== widgetId),
    });
  };

  const handleToggleVisibility = (widgetId) => {
    if (!config || !config.widgets) return;

    setConfig({
      ...config,
      widgets: config.widgets.map(w =>
        w.id === widgetId ? { ...w, is_visible: !w.is_visible } : w
      ),
    });
  };

  const getLayoutFromWidgets = () => {
    if (!config || !config.widgets) return [];

    return config.widgets.map(widget => ({
      i: `widget-${widget.id}`,
      x: widget.position_x,
      y: widget.position_y,
      w: widget.width,
      h: widget.height,
      minW: widget.min_width || 1,
      minH: widget.min_height || 1,
      maxW: widget.max_width || 12,
      maxH: widget.max_height || 12,
    }));
  };

  const getWidgetIcon = (iconName) => {
    const icons = {
      Users: '👥',
      Send: '📤',
      Activity: '📊',
      DollarSign: '💰',
      Wallet: '👛',
      AlertCircle: '⚠️',
      Clock: '⏰',
      HelpCircle: '❓',
      CheckCircle: '✅',
      FileText: '📄',
      Layout: '📋',
      TrendingUp: '📈',
      PieChart: '🥧',
      CreditCard: '💳',
      Award: '🏆',
    };
    return icons[iconName] || '📊';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Grid Manager</h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure dashboard layouts and widgets for each role
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowWidgetLibrary(!showWidgetLibrary)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add Widget
          </button>
          <button
            onClick={handleSaveConfiguration}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      {/* Role Selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Role
        </label>
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {roles.map(role => (
            <option key={role.code} value={role.code}>
              {role.name}
            </option>
          ))}
        </select>
      </div>

      {/* Widget Library Modal */}
      {showWidgetLibrary && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowWidgetLibrary(false)}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Widget Library</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {widgetLibrary.map(widget => (
                <div
                  key={widget.id}
                  onClick={() => handleAddWidget(widget)}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md cursor-pointer transition-all"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{getWidgetIcon(widget.icon)}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900">{widget.widget_name}</h3>
                      <span className="text-xs text-gray-500">{widget.widget_type}</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">{widget.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Grid Layout */}
      {config && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {config.config_name} - {config.columns} Columns
            </h2>
            <div className="text-sm text-gray-600">
              {config.widgets?.length || 0} widgets
            </div>
          </div>

          <Responsive
            className="layout"
            layouts={{ lg: getLayoutFromWidgets() }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: config.columns, md: 3, sm: 2, xs: 1, xxs: 1 }}
            rowHeight={100}
            width={containerWidth}
            onLayoutChange={handleLayoutChange}
            isDraggable={true}
            isResizable={true}
            compactType="vertical"
            preventCollision={false}
          >
            {(config.widgets || []).map(widget => (
              <div
                key={`widget-${widget.id}`}
                className={`bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-gray-300 p-4 ${
                  !widget.is_visible ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{getWidgetIcon(widget.widget_type?.icon)}</span>
                    <span className="font-semibold text-sm text-gray-900">
                      {widget.widget_title}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleToggleVisibility(widget.id)}
                      className="p-1 hover:bg-gray-200 rounded"
                      title={widget.is_visible ? 'Hide' : 'Show'}
                    >
                      {widget.is_visible ? (
                        <Eye className="w-4 h-4 text-gray-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                    <button
                      onClick={() => handleRemoveWidget(widget.id)}
                      className="p-1 hover:bg-red-100 rounded"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {widget.widget_type?.widget_type} • {widget.width}x{widget.height}
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  Position: ({widget.position_x}, {widget.position_y})
                </div>
              </div>
            ))}
          </Responsive>
        </div>
      )}

      {/* Configuration Details */}
      {config && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration Details</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Layout Type</label>
              <input
                type="text"
                value={config.layout_type}
                onChange={(e) => setConfig({ ...config, layout_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Columns</label>
              <input
                type="number"
                value={config.columns}
                onChange={(e) => setConfig({ ...config, columns: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="1"
                max="12"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Gap</label>
              <input
                type="number"
                value={config.gap}
                onChange={(e) => setConfig({ ...config, gap: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="0"
                max="20"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Status</label>
              <span className={`inline-block px-3 py-2 rounded-lg text-sm font-medium ${
                config.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {config.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardGridManager;
